import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Card, CardContent, Chip, Fade, Divider } from '@mui/material';
import { CheckCircle, CalendarToday } from '@mui/icons-material';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';

export interface NaturalMountData {
  data?: any;
  resumo: string;
  url: string;
}

export interface NaturalMountFeedbackProps {
  naturalMountData: NaturalMountData;
  onNavigate?: (url: string) => void;
}

type NaturalMountParsed = {
  data?: any;
  resumo?: string;
  url?: string;
};

const normalizeTextJson = (raw: string) => {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return '';

  const fenceIdx = trimmed.indexOf('```');
  if (fenceIdx === -1) return trimmed;

  const withoutFirstFence = trimmed.replace(/^```(?:json)?\s*/i, '');
  return withoutFirstFence.replace(/\s*```$/, '').trim();
};

const tryParseJson = (raw: unknown): any | undefined => {
  if (raw === null || raw === undefined) return undefined;
  if (typeof raw !== 'string') return undefined;
  const normalized = normalizeTextJson(raw);
  if (!normalized) return undefined;
  try {
    return JSON.parse(normalized);
  } catch {
    return undefined;
  }
};

const normalizeNaturalMountData = (input: any): NaturalMountParsed => {
  if (input && typeof input === 'object' && typeof input.resumo === 'string') {
    return { data: input.data, resumo: input.resumo, url: input.url };
  }

  const parsedFromText = tryParseJson(input?.text);
  if (parsedFromText && typeof parsedFromText === 'object') {
    return {
      data: (parsedFromText as any).data,
      resumo: (parsedFromText as any).resumo,
      url: (parsedFromText as any).url,
    };
  }

  const parsedFromNestedText = tryParseJson(input?.data?.text);
  if (parsedFromNestedText && typeof parsedFromNestedText === 'object') {
    return {
      data: (parsedFromNestedText as any).data,
      resumo: (parsedFromNestedText as any).resumo,
      url: (parsedFromNestedText as any).url,
    };
  }

  return { data: input?.data, resumo: input?.resumo, url: input?.url };
};

const formatDate = (value: string | undefined, locale: string) => {
  const v = (value || '').trim();
  if (!v) return '—';
  const iso = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    const year = Number(iso[1]);
    const monthIndex = Number(iso[2]) - 1;
    const day = Number(iso[3]);
    const date = new Date(Date.UTC(year, monthIndex, day));
    return new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
  }
  return v;
};

const extractFallbackFromResumo = (resumoText: string) => {
  const text = String(resumoText || '');
  const femaleMatch = text.match(/f[êe]mea\s+"([^"]+)"/i) || text.match(/f[êe]mea\s+([#A-Za-z0-9-]+)/i);
  const animalMatch = text.match(/animal\s+"([^"]+)"/i) || text.match(/animal\s+([#A-Za-z0-9-]+)/i);
  const bullMatch = text.match(/touro\s+"([^"]+)"/i) || text.match(/touro\s+([A-Za-zÀ-ÖØ-öø-ÿ'`-]+)/i);
  const dateBrMatch = text.match(/(\d{2}\/\d{2}\/\d{4})/);
  const dateIsoMatch = text.match(/(\d{4}-\d{2}-\d{2})/);

  return {
    femaleId: femaleMatch?.[1] || animalMatch?.[1] || '—',
    bullName: bullMatch?.[1] || '—',
    date: dateBrMatch?.[1] || dateIsoMatch?.[1] || '—',
  };
};

const KeyValueRow = (props: { label: string; value: React.ReactNode }) => {
  const { label, value } = props;
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1 }}>
      <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, fontWeight: 600 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', minWidth: 0 }}>
        {typeof value === 'string' ? (
          <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.primary, fontWeight: 500, textAlign: 'right' }}>
            {value}
          </Typography>
        ) : (
          value
        )}
      </Box>
    </Box>
  );
};

const NaturalMountFeedback: React.FC<NaturalMountFeedbackProps> = ({ naturalMountData }) => {
  const { t, i18n } = useTranslation('farm');
  const parsed = useMemo(() => normalizeNaturalMountData(naturalMountData), [naturalMountData]);
  const resumo = parsed.resumo || naturalMountData?.resumo || '';
  const data = parsed.data || naturalMountData?.data || {};
  const entityProfile = data?.entity_profile || {};
  const cattleId = entityProfile?.cattle_id || {};

  const fallback = useMemo(() => extractFallbackFromResumo(resumo), [resumo]);

  const femaleDisplay =
    entityProfile?.name ||
    cattleId?.name ||
    entityProfile?._key ||
    cattleId?.tag ||
    fallback.femaleId ||
    '—';

  const bullName =
    data?.handling_details_sent?.touro_id ||
    data?.handling_details?.touro_id ||
    data?.handling_details?.touro ||
    fallback.bullName ||
    '—';

  const handlingDate =
    formatDate(data?.handling_date, i18n.language) !== '—'
      ? formatDate(data?.handling_date, i18n.language)
      : formatDate(fallback.date, i18n.language);

  const animalTag = cattleId?.tag ? String(cattleId.tag) : '—';

  return (
    <Fade in timeout={800}>
      <Box
        sx={{
          p: 3,
          height: 'calc(100vh - 60px)',
          overflow: 'auto',
        }}
      >
        <Box sx={{ maxWidth: 920, mx: 'auto' }}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <img
                  src="/vaca.svg"
                  alt={t('reproduction.natural_mount.animalAlt')}
                  style={{
                    width: 32,
                    height: 32,
                    color: DESIGN_TOKENS.colors.organization.farm,
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: DESIGN_TOKENS.colors.organization.farm,
                      mb: 0.5,
                    }}
                  >
                    {t('reproduction.natural_mount.title')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                    {t('reproduction.natural_mount.subtitle')}
                  </Typography>
                </Box>
                <Chip
                  icon={<CheckCircle sx={{ fontSize: 18 }} />}
                  label={t('reproduction.natural_mount.registered')}
                  color="success"
                  sx={{ fontWeight: 600 }}
                />
              </Box>

              {resumo ? (
                <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                  {resumo}
                </Typography>
              ) : null}
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: DESIGN_TOKENS.colors.organization.farm,
                }}
              >
                {t('reproduction.natural_mount.details')}
              </Typography>

              <KeyValueRow
                label={t('reproduction.natural_mount.female')}
                value={
                  <Chip
                    label={femaleDisplay}
                    variant="outlined"
                    sx={{ fontWeight: 600, maxWidth: 360 }}
                  />
                }
              />
              <Divider />
              <KeyValueRow
                label={t('reproduction.natural_mount.tag')}
                value={<Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.primary, fontWeight: 500 }}>{animalTag}</Typography>}
              />
              <Divider />
              <KeyValueRow
                label={t('reproduction.natural_mount.bull')}
                value={
                  <Chip
                    label={String(bullName || '—')}
                    variant="outlined"
                    sx={{ fontWeight: 600, maxWidth: 360 }}
                  />
                }
              />
              <Divider />
              <KeyValueRow
                label={t('reproduction.natural_mount.date')}
                value={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarToday sx={{ fontSize: 18, color: DESIGN_TOKENS.colors.text.secondary }} />
                    <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.primary, fontWeight: 500 }}>
                      {handlingDate}
                    </Typography>
                  </Box>
                }
              />
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Fade>
  );
};

export default NaturalMountFeedback;
